import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, useAuth, signInWithGoogle } from "@/lib/auth";
import { openPaddleEnrollment } from "@/lib/paddle";
import BackToSiteBadge from "@/components/site/BackToSiteBadge";

const COUNTRIES = [
  "Tanzania", "Kenya", "Uganda", "Rwanda", "Burundi", "Ethiopia",
  "Nigeria", "Ghana", "South Africa", "Zambia", "Malawi", "Other",
];

export default function AffiliateSignup() {
  const { user, affiliate, loading } = useAuth();
  const [terms, setTerms] = useState(null);
  const [step, setStep] = useState(1); // 1 = details, 2 = payment
  const [form, setForm] = useState({
    phone: "",
    country: "Tanzania",
    accepted_terms: false,
  });
  const [stepErrors, setStepErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [paddleStatus, setPaddleStatus] = useState(null); // null | 'starting' | 'opened' | 'completed'
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/affiliate/terms`).then((r) => setTerms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (affiliate?.status === "active") {
      navigate("/affiliate/dashboard", { replace: true });
    }
  }, [affiliate, navigate]);

  const feeUsd = ((terms?.settings?.enrollment_fee_cents || 299) / 100).toFixed(2);

  const validateStep1 = () => {
    const errs = {};
    if (!form.phone || form.phone.replace(/[^\d]/g, "").length < 7) {
      errs.phone = "Enter a valid phone number so our team can reach you about payouts.";
    }
    if (!form.accepted_terms) {
      errs.accepted_terms = "Please accept the program terms to continue.";
    }
    if (!terms?.version) {
      errs.general = "Program terms are still loading. Please wait a moment.";
    }
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep1();
    setStepErrors(errs);
    if (Object.keys(errs).length === 0) {
      setError(null);
      setStep(2);
    }
  };

  const startEnrollment = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // Payout details are collected later from the dashboard. We send
      // payout_method = "pending" so the backend accepts the row.
      const payload = {
        phone: form.phone,
        country: form.country,
        payout_method: "pending",
        payout_mobile_provider: null,
        payout_mobile_number: null,
        payout_bank_name: null,
        payout_bank_account: null,
        payout_bank_holder: null,
        accepted_terms: form.accepted_terms,
        terms_version: terms.version,
      };
      const res = await axios.post(`${API}/affiliate/enroll`, payload, { withCredentials: true });
      if (!res.data?.enrollment_price_id) {
        setError("The affiliate enrollment product is not yet configured. Please come back shortly.");
        setSubmitting(false);
        return;
      }
      setPaddleStatus("starting");
      try {
        await openPaddleEnrollment({
          priceId: res.data.enrollment_price_id,
          email: user.email,
          affiliateId: res.data.affiliate_id,
          userId: user.user_id,
          onEvent: (evt) => {
            if (evt?.name === "checkout.closed") setPaddleStatus(null);
            if (evt?.name === "checkout.completed") setPaddleStatus("completed");
          },
        });
        setPaddleStatus("opened");
      } catch (overlayErr) {
        setError(overlayErr?.message || "Payment is not available right now. Please try again later.");
        setPaddleStatus(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start enrollment. Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <SplashLoader />;

  if (!user) {
    return (
      <Shell title="Join the Milk2Income affiliate program">
        <p className="text-[var(--muted)] mb-8 max-w-md">
          Sign in with Google. We only use your email and name to create your affiliate account.
        </p>
        <button
          onClick={() => signInWithGoogle("/affiliate/dashboard")}
          className="inline-flex items-center gap-3 bg-white border border-[rgba(15,31,23,0.15)] px-6 py-3 rounded-full font-medium hover:shadow-md transition"
          data-testid="aff-signup-google"
        >
          <GoogleG />
          Continue with Google
        </button>
        <p className="mt-6 text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase">
          By continuing you agree to the <Link to="/affiliate/terms" className="underline">program terms</Link>.
        </p>
      </Shell>
    );
  }

  return (
    <Shell title={affiliate ? "Finish your enrollment" : "Create your affiliate account"}>
      <div className="mb-5 flex items-center gap-3 text-sm text-[var(--muted)]">
        Signed in as <strong className="text-[var(--ink)]" data-testid="aff-signup-email">{user.email}</strong>
      </div>

      {/* Mini product summary — mirrors the ebook checkout card */}
      <div className="mb-6 p-4 rounded-2xl border border-[rgba(15,31,23,0.08)] bg-white/70 flex items-center gap-4" data-testid="aff-signup-product-card">
        <div className="w-14 h-14 rounded-xl bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center font-display text-sm leading-tight text-center px-1">
          Affiliate<br/>Pass
        </div>
        <div className="flex-1">
          <div className="font-medium">Milk2Income Affiliate Enrollment</div>
          <div className="text-[12px] text-[var(--muted)]">One-time · 5% commissions · 30-day attribution</div>
        </div>
        <div className="text-right">
          <div className="font-display text-xl" data-testid="aff-signup-price">${feeUsd}</div>
          <div className="text-[10px] font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)]">USD · one time</div>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-6 flex items-center gap-2 text-[10px] font-mono-caption tracking-[0.22em] uppercase text-[var(--muted)]" data-testid="aff-signup-stepper">
        <span className={step === 1 ? "text-[var(--marigold)]" : ""}>1 · Your details</span>
        <span className="opacity-40">—</span>
        <span className={step === 2 ? "text-[var(--marigold)]" : ""}>2 · Payment</span>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="s1"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25 }}
            className="space-y-5"
            data-testid="aff-signup-step1"
          >
            <Field label="Phone number" error={stepErrors.phone}>
              <input
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+255 712 345 678"
                className="input-base"
                data-testid="aff-signup-phone"
              />
            </Field>

            <Field label="Country">
              <select
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="input-base"
                data-testid="aff-signup-country"
              >
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>

            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.accepted_terms}
                onChange={(e) => setForm({ ...form, accepted_terms: e.target.checked })}
                className="mt-1"
                data-testid="aff-signup-accept"
              />
              <span>
                I accept the <Link to="/affiliate/terms" className="underline font-medium">Milk2Income affiliate program terms</Link> (version {terms?.version || "…"}).
              </span>
            </label>
            {stepErrors.accepted_terms && (
              <p className="text-xs text-red-700 -mt-3" data-testid="aff-signup-terms-error">{stepErrors.accepted_terms}</p>
            )}

            {stepErrors.general && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{stepErrors.general}</div>
            )}

            <button
              type="button"
              onClick={handleNext}
              className="w-full py-4 rounded-full bg-[var(--ink)] text-[var(--cream)] font-medium inline-flex items-center justify-center gap-2 hover:bg-black/90 transition"
              data-testid="aff-signup-next"
            >
              Continue
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <p className="text-xs text-[var(--muted)]">
              We'll ask for your payout details from your dashboard after Paddle confirms your enrollment. That way nothing is wasted if payment is interrupted.
            </p>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="s2"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25 }}
            data-testid="aff-signup-step2"
          >
            <div className="font-mono-caption text-[10px] tracking-[0.22em] text-[var(--marigold)] uppercase mb-2">
              Step 2 of 2 · Choose payment
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-6">
              Pay{" "}
              <span className="font-editorial-italic text-[var(--marigold)]">${feeUsd}</span>
              {" "}— one time, no subscription.
            </h3>

            <div className="mb-6 rounded-2xl border border-[rgba(15,31,23,0.1)] p-5 bg-[var(--cream-2)]/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[var(--jade)]/15 text-[var(--jade)] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.8" fill="none"/>
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Secure checkout via Paddle</div>
                  <div className="text-[12px] text-[var(--muted)]">
                    Card, Google Pay, Apple Pay, PayPal, and local methods.
                  </div>
                </div>
              </div>
              <p className="text-[12px] text-[var(--muted)]">
                Your email &amp; phone are sent to Paddle only to confirm your enrollment and activate your affiliate dashboard. Card data never touches our servers.
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-800 p-3 text-[13px]" data-testid="aff-signup-error">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setError(null); }}
                disabled={submitting || paddleStatus === "opened"}
                className="px-6 py-3 rounded-full border border-[rgba(15,31,23,0.2)] text-[var(--ink)] sm:flex-shrink-0 disabled:opacity-50"
                data-testid="aff-signup-back"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={startEnrollment}
                disabled={submitting || paddleStatus === "opened"}
                className="flex-1 py-3 rounded-full bg-[var(--marigold)] text-white font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="aff-signup-submit"
              >
                {submitting
                  ? "Starting enrollment…"
                  : paddleStatus === "opened"
                  ? "Complete payment in the Paddle overlay"
                  : `Continue to Paddle · $${feeUsd}`}
              </button>
            </div>
            <p className="text-xs text-[var(--muted)] mt-3">
              Taxes handled by Paddle at checkout per your region. Your account activates automatically once our server confirms your payment.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </Shell>
  );
}

function Field({ label, children, error }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  );
}

function SplashLoader() {
  return (
    <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-4 border-[var(--marigold)] border-t-transparent animate-spin" />
    </div>
  );
}

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)] px-6 md:px-10 py-10">
      <div className="max-w-xl mx-auto">
        <div className="mb-8">
          <BackToSiteBadge />
        </div>
        <Link to="/affiliate" className="inline-flex items-center gap-2 text-sm text-[var(--muted)] mb-6 hover:text-[var(--marigold)]" data-testid="aff-signup-back-program">
          ← Back to program
        </Link>
        <h1 className="font-display text-3xl md:text-4xl mb-6">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6.5 29.4 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6.5 29.4 4.5 24 4.5 16.2 4.5 9.4 8.8 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 43.5c5.3 0 10.1-1.8 13.9-4.9l-6.4-5.2c-2 1.4-4.5 2.1-7.5 2.1-5.3 0-9.7-3.4-11.3-8l-6.6 5C9.3 39.1 16.1 43.5 24 43.5z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6.4 5.2C40 36.5 43.5 30.9 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}
